<template>
  <section
    :class="[
      props.frameless
        ? 'bg-transparent overflow-hidden'
        : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
    ]"
  >
    <div :class="props.frameless ? 'pt-1 pb-2' : 'px-6 pt-5 pb-4 border-b border-gray-200'">
      <div>
        <h2 class="mb-0 text-xl font-semibold leading-tight text-black">Meilleurs acheteurs</h2>
        <p class="mb-0 mt-2 text-sm leading-tight text-gray-500">{{ periodLabel }}</p>
      </div>
    </div>

    <div :class="props.frameless ? 'pt-3 pb-5' : 'px-6 py-5'">
      <div class="grid grid-cols-[minmax(0,1fr)_82px_104px] items-end gap-3 border-b-2 border-gray-400 pb-2 text-sm sm:grid-cols-[minmax(0,1fr)_104px_120px] sm:gap-4">
        <div class="font-medium text-black">Acheteurs</div>
        <div class="text-right">
          <div class="font-medium text-black">Commandes</div>
          <div class="text-xs text-black">{{ totalOrders }}</div>
        </div>
        <div class="text-right">
          <div class="font-medium text-black">Montant (HT)</div>
          <div class="text-xs text-black">{{ formatCurrency(totalAmount) }}</div>
        </div>
      </div>

      <div v-if="loading" class="space-y-4 py-6">
        <div v-for="index in 5" :key="index" class="animate-pulse">
          <div class="grid grid-cols-[minmax(0,1fr)_82px_104px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_104px_120px] sm:gap-4">
            <div class="flex items-center gap-3">
              <div class="h-9 w-9 rounded bg-gray-200"></div>
              <div class="h-3 w-2/3 rounded bg-gray-200"></div>
            </div>
            <div class="ml-auto h-3 w-10 rounded bg-gray-200"></div>
            <div class="ml-auto h-3 w-16 rounded bg-gray-200"></div>
          </div>
          <div class="mt-3 h-1 rounded bg-gray-100"></div>
        </div>
      </div>

      <div v-else-if="rows.length === 0" class="py-10 text-center text-sm text-gray-500">
        Aucun acheteur sur cette période.
      </div>

      <div v-else class="divide-y divide-gray-100">
        <article v-for="row in rows" :key="row.id" class="py-4">
          <div class="grid grid-cols-[minmax(0,1fr)_82px_104px] items-center gap-3 sm:grid-cols-[minmax(0,1fr)_104px_120px] sm:gap-4">
            <div class="min-w-0 flex items-center gap-3">
              <div
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-semibold text-slate-700 ring-1 ring-slate-200"
                :style="{ backgroundColor: getAvatarColor(row.id) }"
              >
                {{ getInitials(row.nom) }}
              </div>
              <div class="min-w-0">
                <div class="truncate text-sm font-medium text-black">{{ row.nom }}</div>
                <div class="truncate text-xs text-gray-500">{{ getBuyerLabel(row.commandes) }}</div>
              </div>
            </div>

            <div class="text-right text-sm font-medium text-black">{{ row.commandes }}</div>
            <div class="text-right text-sm font-medium text-black">{{ row.montant }}</div>
          </div>

          <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              class="h-full rounded-full bg-blue-700"
              :style="{ width: `${getWidth(row.montant)}%` }"
            ></div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { AcheteurRow } from '@/types'

const props = withDefaults(defineProps<{
  rows: AcheteurRow[]
  loading?: boolean
  periodLabel: string
  frameless?: boolean
}>(), {
  rows: () => [],
  loading: false,
  frameless: false,
})

const parseAmount = (value: number | string) => {
  if (typeof value === 'number') return value
  const normalized = String(value || '0')
    .replace(/\s/g, '')
    .replace('€', '')
    .replace(',', '.')
  return Number.parseFloat(normalized) || 0
}

const totalOrders = computed(() =>
  props.rows.reduce((total, row) => total + Number(row.commandes || 0), 0),
)

const totalAmount = computed(() =>
  props.rows.reduce((total, row) => total + parseAmount(row.montant), 0),
)

const maxAmount = computed(() =>
  Math.max(1, ...props.rows.map((row) => parseAmount(row.montant))),
)

const formatCurrency = (value: number) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const getWidth = (amount: number | string) =>
  Math.max(3, Math.round((parseAmount(amount) / maxAmount.value) * 100))

const getInitials = (name: string) => {
  const words = (name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  const initials = words.slice(0, 2).map((word) => word.charAt(0)).join('')
  return initials.toUpperCase() || '?'
}

const getBuyerLabel = (orders: number) =>
  `${orders} ${orders > 1 ? 'commandes' : 'commande'}`

const getAvatarColor = (id: number) => {
  const colors = ['#e0f2fe', '#dcfce7', '#fef3c7', '#ede9fe', '#fee2e2']
  return colors[Math.abs(Number(id || 0)) % colors.length]
}
</script>
